import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Package, ShoppingBag, Truck, CheckCircle, XCircle, Clock, MessageCircle,
  Send, ChevronRight, Calendar, DollarSign, User, RefreshCw, Star
} from "lucide-react";
import { getUserPurchases, getUserSales, getOrder, updateOrderStatus, getOrderMessages, addOrderMessage, resolveImageUrl, getOrderReview } from "@/lib/api";
import { ReviewButton } from "@/components/ReviewDialog";
import { toast } from "sonner";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-800 border-blue-200", icon: CheckCircle },
  shipped: { label: "Shipped", color: "bg-purple-100 text-purple-800 border-purple-200", icon: Truck },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle }
};

function OrderCard({ order, isSeller, onViewDetails, onUpdateStatus, onReviewSubmitted }) {
  const [hasReviewed, setHasReviewed] = useState(order.has_review || false);
  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const otherParty = isSeller ? order.buyer : order.seller;
  const listing = order.listing;
  
  // Check if buyer can leave review (order is delivered and not yet reviewed)
  const canReview = !isSeller && order.status === "delivered" && !hasReviewed;

  return (
    <Card className="bg-white border-[#E5E0D5] hover:shadow-md transition-shadow" data-testid={`order-card-${order.order_id}`}>
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Listing Image */}
          <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#F0EBE0] flex-shrink-0">
            {listing?.image_url ? (
              <img src={resolveImageUrl(listing.image_url)} alt={listing.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="w-8 h-8 text-[#6B665E]" />
              </div>
            )}
          </div>

          {/* Order Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-['Nunito'] font-semibold text-[#2D2A26] truncate" data-testid={`order-title-${order.order_id}`}>
                  {listing?.title || "Item"}
                </h3>
                <p className="text-xs text-[#6B665E] font-['Nunito'] mt-0.5">
                  Order #{order.order_id.slice(-8).toUpperCase()}
                </p>
              </div>
              <Badge className={`${statusConfig.color} border font-['Nunito'] text-xs flex items-center gap-1`} data-testid={`order-status-${order.order_id}`}>
                <StatusIcon className="w-3 h-3" />
                {statusConfig.label}
              </Badge>
            </div>

            {/* Price and User */}
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-[var(--liturgical-primary)]" />
                <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)]">${order.amount.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Avatar className="h-5 w-5">
                  <AvatarImage src={otherParty?.picture} />
                  <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">{otherParty?.name?.[0]}</AvatarFallback>
                </Avatar>
                <span className="text-xs text-[#6B665E] font-['Nunito'] truncate">
                  {isSeller ? "Buyer: " : "Seller: "}{otherParty?.name}
                </span>
              </div>
            </div>

            {/* Tracking Info */}
            {order.tracking_number && (
              <div className="flex items-center gap-1.5 mt-2">
                <Truck className="w-3.5 h-3.5 text-[#6B665E]" />
                <span className="text-xs text-[#6B665E] font-['Nunito']">
                  {order.carrier && `${order.carrier}: `}{order.tracking_number}
                </span>
              </div>
            )}

            {/* Date and Actions */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-1 text-[10px] text-[#6B665E] font-['Nunito']">
                <Calendar className="w-3 h-3" />
                {new Date(order.created_at).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-2">
                {isSeller && order.status === "pending" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs font-['Nunito'] border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10"
                    onClick={() => onUpdateStatus(order, "confirmed")}
                    data-testid={`confirm-order-${order.order_id}`}
                  >
                    Confirm
                  </Button>
                )}
                {isSeller && order.status === "confirmed" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs font-['Nunito'] border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10"
                    onClick={() => onUpdateStatus(order, "shipped")}
                    data-testid={`ship-order-${order.order_id}`}
                  >
                    Mark Shipped
                  </Button>
                )}
                {!isSeller && order.status === "shipped" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs font-['Nunito'] border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10"
                    onClick={() => onUpdateStatus(order, "delivered")}
                    data-testid={`confirm-delivery-${order.order_id}`}
                  >
                    Confirm Delivery
                  </Button>
                )}
                {canReview && (
                  <ReviewButton 
                    order={order} 
                    hasReviewed={hasReviewed}
                    onReviewSubmitted={() => {
                      setHasReviewed(true);
                      if (onReviewSubmitted) onReviewSubmitted();
                    }}
                  />
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs font-['Nunito'] text-[#6B665E] hover:text-[#2D2A26]"
                  onClick={() => onViewDetails(order)}
                  data-testid={`view-order-${order.order_id}`}
                >
                  Details <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OrderDetailDialog({ order, isSeller, onClose, onStatusUpdate }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showShipDialog, setShowShipDialog] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (order) loadMessages();
  }, [order]);

  const loadMessages = async () => {
    setLoadingMessages(true);
    try {
      const res = await getOrderMessages(order.order_id);
      setMessages(res.data);
    } catch {
      // Silent fail
    }
    setLoadingMessages(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await addOrderMessage(order.order_id, newMessage);
      setNewMessage("");
      loadMessages();
      toast.success("Message sent");
    } catch {
      toast.error("Failed to send message");
    }
    setSendingMessage(false);
  };

  const handleStatusUpdate = async (status, extras = {}) => {
    setUpdatingStatus(true);
    try {
      await updateOrderStatus(order.order_id, { status, ...extras });
      toast.success(`Order ${status}`);
      onStatusUpdate();
      if (status === "shipped") setShowShipDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
    setUpdatingStatus(false);
  };

  if (!order) return null;

  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const otherParty = isSeller ? order.buyer : order.seller;
  const listing = order.listing;

  return (
    <>
      <Dialog open={!!order} onOpenChange={() => onClose()}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
              Order Details
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Order Summary */}
            <div className="bg-[#F9F7F2] rounded-lg p-4">
              <div className="flex gap-4">
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-white flex-shrink-0">
                  {listing?.image_url ? (
                    <img src={resolveImageUrl(listing.image_url)} alt={listing.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-10 h-10 text-[#6B665E]" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-['Nunito'] font-bold text-lg text-[#2D2A26]">{listing?.title}</h3>
                  <p className="text-sm text-[#6B665E] font-['Nunito'] line-clamp-2">{listing?.description}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="font-['Nunito'] font-bold text-lg text-[var(--liturgical-primary)]">${order.amount.toFixed(2)}</span>
                    <Badge className={`${statusConfig.color} border font-['Nunito'] text-xs flex items-center gap-1`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig.label}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Parties */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#F9F7F2] rounded-lg p-3">
                <p className="text-xs text-[#6B665E] font-['Nunito'] mb-2">Seller</p>
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={order.seller?.picture} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">{order.seller?.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-['Nunito'] font-semibold text-[#2D2A26]">{order.seller?.name}</p>
                    <p className="text-xs text-[#6B665E] font-['Nunito']">{order.seller?.email}</p>
                  </div>
                </div>
              </div>
              <div className="bg-[#F9F7F2] rounded-lg p-3">
                <p className="text-xs text-[#6B665E] font-['Nunito'] mb-2">Buyer</p>
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={order.buyer?.picture} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">{order.buyer?.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-['Nunito'] font-semibold text-[#2D2A26]">{order.buyer?.name}</p>
                    <p className="text-xs text-[#6B665E] font-['Nunito']">{order.buyer?.email}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tracking Info */}
            {order.tracking_number && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-['Nunito'] font-semibold text-purple-800">Shipping Info</span>
                </div>
                <p className="text-sm text-purple-700 font-['Nunito'] mt-1">
                  {order.carrier && <span className="font-semibold">{order.carrier}: </span>}
                  {order.tracking_number}
                </p>
              </div>
            )}

            {/* Order Timeline */}
            <div>
              <h4 className="text-sm font-['Nunito'] font-semibold text-[#2D2A26] mb-2">Order Timeline</h4>
              <div className="space-y-2">
                {order.status_history?.map((entry, idx) => {
                  const entryConfig = STATUS_CONFIG[entry.status] || STATUS_CONFIG.pending;
                  return (
                    <div key={entry.history_id || idx} className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${entryConfig.color}`}>
                        <entryConfig.icon className="w-3 h-3" />
                      </div>
                      <div>
                        <p className="text-sm font-['Nunito'] font-semibold text-[#2D2A26]">{entryConfig.label}</p>
                        <p className="text-xs text-[#6B665E] font-['Nunito']">
                          {new Date(entry.created_at).toLocaleString()}
                          {entry.notes && ` - ${entry.notes}`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Messages */}
            <div>
              <h4 className="text-sm font-['Nunito'] font-semibold text-[#2D2A26] mb-2 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Messages
              </h4>
              <div className="bg-[#F9F7F2] rounded-lg p-3">
                <ScrollArea className="h-40">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <RefreshCw className="w-5 h-5 animate-spin text-[#6B665E]" />
                    </div>
                  ) : messages.length === 0 ? (
                    <p className="text-sm text-[#6B665E] font-['Nunito'] text-center py-8">No messages yet</p>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg) => (
                        <div key={msg.message_id} className="flex items-start gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={msg.sender?.picture} />
                            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">{msg.sender?.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="text-xs font-['Nunito'] font-semibold text-[#2D2A26]">{msg.sender?.name}</span>
                              <span className="text-[10px] text-[#6B665E]">{new Date(msg.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-[#2D2A26] font-['Nunito']">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                <div className="flex gap-2 mt-3 pt-3 border-t border-[#E5E0D5]">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 text-sm font-['Nunito'] border-[#E5E0D5]"
                    onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                    data-testid="order-message-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !newMessage.trim()}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90"
                    data-testid="send-order-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E0D5]">
            {isSeller && order.status === "pending" && (
              <Button
                onClick={() => handleStatusUpdate("confirmed")}
                disabled={updatingStatus}
                className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                data-testid="detail-confirm-btn"
              >
                Confirm Order
              </Button>
            )}
            {isSeller && order.status === "confirmed" && (
              <Button
                onClick={() => setShowShipDialog(true)}
                disabled={updatingStatus}
                className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                data-testid="detail-ship-btn"
              >
                <Truck className="w-4 h-4 mr-2" /> Mark as Shipped
              </Button>
            )}
            {!isSeller && order.status === "shipped" && (
              <Button
                onClick={() => handleStatusUpdate("delivered")}
                disabled={updatingStatus}
                className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                data-testid="detail-delivered-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Confirm Delivery
              </Button>
            )}
            {order.status !== "delivered" && order.status !== "cancelled" && (
              <Button
                variant="outline"
                onClick={() => handleStatusUpdate("cancelled")}
                disabled={updatingStatus || (order.status === "shipped" && !isSeller)}
                className="border-red-300 text-red-600 hover:bg-red-50 font-['Nunito']"
                data-testid="detail-cancel-btn"
              >
                Cancel Order
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ship Dialog */}
      <Dialog open={showShipDialog} onOpenChange={setShowShipDialog}>
        <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
              <Truck className="w-5 h-5 inline mr-2" /> Shipping Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-['Nunito'] text-[#2D2A26]">Carrier (Optional)</Label>
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito']">
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E5E0D5]">
                  <SelectItem value="USPS">USPS</SelectItem>
                  <SelectItem value="UPS">UPS</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-['Nunito'] text-[#2D2A26]">Tracking Number (Optional)</Label>
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter tracking number"
                className="border-[#E5E0D5] font-['Nunito']"
                data-testid="tracking-number-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShipDialog(false)} className="font-['Nunito']">
              Cancel
            </Button>
            <Button
              onClick={() => handleStatusUpdate("shipped", { tracking_number: trackingNumber, carrier })}
              disabled={updatingStatus}
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
              data-testid="confirm-ship-btn"
            >
              {updatingStatus ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Confirm Shipment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function MyOrdersPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "purchases");
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailedOrder, setDetailedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const [purchasesRes, salesRes] = await Promise.all([
        getUserPurchases(),
        getUserSales()
      ]);
      setPurchases(purchasesRes.data);
      setSales(salesRes.data);
    } catch (err) {
      toast.error("Failed to load orders");
    }
    setLoading(false);
  };

  const handleViewDetails = async (order) => {
    try {
      const res = await getOrder(order.order_id);
      setDetailedOrder(res.data);
      setSelectedOrder(order);
    } catch {
      toast.error("Failed to load order details");
    }
  };

  const handleQuickStatusUpdate = async (order, status) => {
    try {
      await updateOrderStatus(order.order_id, { status });
      toast.success(`Order ${status}`);
      loadOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const purchaseStats = {
    total: purchases.length,
    pending: purchases.filter(o => o.status === "pending" || o.status === "confirmed").length,
    shipped: purchases.filter(o => o.status === "shipped").length,
    delivered: purchases.filter(o => o.status === "delivered").length
  };

  const saleStats = {
    total: sales.length,
    pending: sales.filter(o => o.status === "pending").length,
    toShip: sales.filter(o => o.status === "confirmed").length,
    shipped: sales.filter(o => o.status === "shipped").length,
    completed: sales.filter(o => o.status === "delivered").length
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="my-orders-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26]" data-testid="orders-page-title">
            My Orders
          </h1>
          <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
            Track your purchases and manage your sales
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-4 text-center">
              <ShoppingBag className="w-6 h-6 text-[var(--liturgical-primary)] mx-auto mb-2" />
              <p className="text-2xl font-['Nunito'] font-bold text-[#2D2A26]">{purchaseStats.total}</p>
              <p className="text-xs text-[#6B665E] font-['Nunito']">Total Purchases</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-4 text-center">
              <Truck className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-['Nunito'] font-bold text-[#2D2A26]">{purchaseStats.shipped}</p>
              <p className="text-xs text-[#6B665E] font-['Nunito']">In Transit</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-4 text-center">
              <Package className="w-6 h-6 text-[var(--liturgical-secondary)] mx-auto mb-2" />
              <p className="text-2xl font-['Nunito'] font-bold text-[#2D2A26]">{saleStats.total}</p>
              <p className="text-xs text-[#6B665E] font-['Nunito']">Total Sales</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-['Nunito'] font-bold text-[#2D2A26]">{saleStats.pending + saleStats.toShip}</p>
              <p className="text-xs text-[#6B665E] font-['Nunito']">Need Action</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border border-[#E5E0D5] p-1">
            <TabsTrigger
              value="purchases"
              className="font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white"
              data-testid="purchases-tab"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              My Purchases ({purchases.length})
            </TabsTrigger>
            <TabsTrigger
              value="sales"
              className="font-['Nunito'] data-[state=active]:bg-[var(--liturgical-primary)] data-[state=active]:text-white"
              data-testid="sales-tab"
            >
              <Package className="w-4 h-4 mr-2" />
              My Sales ({sales.length})
            </TabsTrigger>
          </TabsList>

          {/* Purchases Tab */}
          <TabsContent value="purchases" className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" />
              </div>
            ) : purchases.length === 0 ? (
              <Card className="bg-white border-[#E5E0D5]">
                <CardContent className="py-12 text-center">
                  <ShoppingBag className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
                  <h3 className="font-['Nunito'] font-semibold text-[#2D2A26] mb-2">No purchases yet</h3>
                  <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">
                    Browse the marketplace to find items from your parish community
                  </p>
                  <Button
                    onClick={() => navigate("/marketplace")}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                    data-testid="browse-marketplace-btn"
                  >
                    Browse Marketplace
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {purchases.map((order) => (
                  <OrderCard
                    key={order.order_id}
                    order={order}
                    isSeller={false}
                    onViewDetails={handleViewDetails}
                    onUpdateStatus={handleQuickStatusUpdate}
                    onReviewSubmitted={loadOrders}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sales Tab */}
          <TabsContent value="sales" className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" />
              </div>
            ) : sales.length === 0 ? (
              <Card className="bg-white border-[#E5E0D5]">
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
                  <h3 className="font-['Nunito'] font-semibold text-[#2D2A26] mb-2">No sales yet</h3>
                  <p className="text-sm text-[#6B665E] font-['Nunito'] mb-4">
                    Create a listing to start selling to your parish community
                  </p>
                  <Button
                    onClick={() => navigate("/marketplace")}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                    data-testid="create-listing-btn"
                  >
                    Create Listing
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {sales.map((order) => (
                  <OrderCard
                    key={order.order_id}
                    order={order}
                    isSeller={true}
                    onViewDetails={handleViewDetails}
                    onUpdateStatus={handleQuickStatusUpdate}
                    onReviewSubmitted={loadOrders}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Order Detail Dialog */}
      <OrderDetailDialog
        order={detailedOrder}
        isSeller={detailedOrder?.seller_id === user?.user_id}
        onClose={() => {
          setSelectedOrder(null);
          setDetailedOrder(null);
        }}
        onStatusUpdate={() => {
          loadOrders();
          // Refresh the detailed order
          if (selectedOrder) handleViewDetails(selectedOrder);
        }}
      />
    </div>
  );
}
